import { Tooltip, TooltipContent, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useNotify } from '@newfront-insurance/next-notifications';
import { Download, ExternalLink, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { downloadPreviewFile } from './download-preview-file';

const toolbarActionClassName =
  'flex size-7 shrink-0 items-center justify-center rounded-sm text-[#546a83] transition-colors hover:bg-[#f4f5f6] hover:text-[#262d46]';

function ToolbarActionTooltip({ children, label }: { children: ReactNode; label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

export function DocumentPreviewToolbarActions({
  filename,
  fileUrl,
  onClose,
  showOpenInNewTab = false,
}: {
  filename: string;
  fileUrl: string;
  onClose: () => void;
  showOpenInNewTab?: boolean;
}) {
  const notify = useNotify();
  const downloadController = useRef<AbortController | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    return () => {
      downloadController.current?.abort();
      downloadController.current = null;
    };
  }, [fileUrl]);

  const download = async () => {
    if (downloadController.current) return;
    const controller = new AbortController();
    downloadController.current = controller;
    setIsDownloading(true);
    try {
      await downloadPreviewFile(fileUrl, filename, controller.signal);
    } catch {
      if (!controller.signal.aborted) notify.error('Unable to download the file. Reopen the preview and try again.');
    } finally {
      if (!downloadController.current || downloadController.current === controller) {
        downloadController.current = null;
        setIsDownloading(false);
      }
    }
  };

  return (
    <>
      <ToolbarActionTooltip label="Download file">
        <button
          type="button"
          disabled={isDownloading}
          aria-busy={isDownloading}
          onClick={() => void download()}
          aria-label="Download preview file"
          className={toolbarActionClassName}
        >
          {isDownloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        </button>
      </ToolbarActionTooltip>
      {showOpenInNewTab ? (
        <ToolbarActionTooltip label="Preview in new tab">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open preview file in a new tab"
            className={toolbarActionClassName}
          >
            <ExternalLink className="size-4" />
          </a>
        </ToolbarActionTooltip>
      ) : null}
      <ToolbarActionTooltip label="Close">
        <button type="button" aria-label="Close preview" className={toolbarActionClassName} onClick={onClose}>
          <X className="size-4" />
        </button>
      </ToolbarActionTooltip>
    </>
  );
}
